import { Component, inject, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, signal, Output, EventEmitter, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CIPXShoppingCartService } from './shopping-cart.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { CIPXScrollableContentComponent } from '../ui/scrollable-content/scrollable-content.component';
import { CIPXActionBarComponent } from '../ui/action-bar/action-bar.component';
import { CIPXCancelButtonComponent } from '../ui/buttons/cancel-button/cancel-button.component';
import { CIPXPrimaryButtonComponent } from '../ui/buttons/primary-button/primary-button.component';
import { CIPXShoppingCartItemComponent } from './shopping-cart-item/shopping-cart-item.component';
import { CIPXQuoteRequestCartItemComponent } from './shopping-cart-item/quote-request-cart-item/quote-request-cart-item.component';
import { CIPXRiderCartItemComponent } from './shopping-cart-item/rider-cart-item/rider-cart-item.component';
import { CIPXCartSummaryComponent } from './cart-summary/cart-summary.component';
import { CIPXShoppingCartPaymentComponent } from './payment/shopping-cart-payment.component';
import { CIPXShoppingCartConfirmationComponent } from './confirmation/shopping-cart-confirmation.component';
import { ShoppingCartItem, CartItemType, isQuoteRequestItem, isRiderItem, ShoppingCartMessageType } from './shopping-cart.types';
import { ShoppingCartStep } from './shopping-cart-steps.enum';
import { PaymentProcessResult } from './payment/shopping-cart-payment.types';
import { Router } from '@angular/router';
import { OpenQuoteRequestsWidgetService } from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import { QuoteRequestsListService } from '@sbd-account-portal/app/modules/quote/requests-list/quote-requests-list.service';
import { CathyService } from '@sbd-account-portal/app/services/cathy.service';
import {
  MessageComposeDrawerWrapperComponent
} from '@sbd-account-portal/app/modules/tasksets/message-compose/message-compose-drawer-wrapper.component';
import { CIPXDrawerManagerService } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { UserService } from '@sbd-account-portal/app/core/user/user.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';

@Component({
  selector: 'cipx-shopping-cart',
  templateUrl: './shopping-cart.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    CIPXScrollableContentComponent,
    CIPXActionBarComponent,
    CIPXCancelButtonComponent,
    CIPXPrimaryButtonComponent,
    CIPXShoppingCartItemComponent,
    CIPXQuoteRequestCartItemComponent,
    CIPXRiderCartItemComponent,
    CIPXCartSummaryComponent,
    CIPXShoppingCartPaymentComponent,
    CIPXShoppingCartConfirmationComponent,
  ],
  styleUrl: './shopping-cart.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CIPXShoppingCartComponent {
  // !!! TODO: we need to better handle the dependency injection for this service.
  _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);
  _quoteRequestsListService = inject(QuoteRequestsListService);
  _cathyService = inject(CathyService);
// !!! TODO: we need to better handle the dependency injection for this service.

  _drawerService = inject(CIPXDrawerManagerService);

  private _userService = inject(UserService);
  private _fuseConfirmationService = inject(FuseConfirmationService);
  private _router = inject(Router);
  private _shoppingCartService = inject(CIPXShoppingCartService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  @ViewChild('shoppingCartScroll', { static: false, read: ElementRef }) shoppingCartScroll!: ElementRef;
  @ViewChild(CIPXShoppingCartPaymentComponent) private _paymentComponent!: CIPXShoppingCartPaymentComponent;

  items = this._shoppingCartService.items;
  showCancelButton = true;

  // Step management
  currentStep = signal<ShoppingCartStep>(ShoppingCartStep.CART);

  // Events for drawer integration
  @Output() stepChanged = new EventEmitter<{
    step: ShoppingCartStep;
    title: string;
    showBackButton: boolean;
    canGoBack: boolean;
  }>();

  @Output() closeDrawer = new EventEmitter<void>();
  @Output() overlaySettingsChanged = new EventEmitter<{ transparentOverlay: boolean; disableOverlay: boolean }>();

  // Expose enums to template
  CartItemType = CartItemType;
  ShoppingCartStep = ShoppingCartStep;

  // Payment state
  public isPaymentFormValid = false;
  public paymentProcessing = false;
  public paymentResult: PaymentProcessResult | null = null;

  // Confirmation state
  public orderNumber: string | null = null;

  constructor() {
    // Watch for step changes and emit events for drawer integration
    effect(() => {
      const step = this.currentStep();
      this.emitStepChangeEvent(step);
    });
  }

  onCancel() {
    this._shoppingCartService.closeDrawer();
  }

  canProceed() {
    switch (this.currentStep()) {
      case ShoppingCartStep.CART:
        return this.items().length > 0;
      case ShoppingCartStep.PAYMENT:
        return this.isPaymentFormValid && !this.paymentProcessing;
      case ShoppingCartStep.CONFIRMATION:
        return false; // No action needed on confirmation step
      default:
        return false;
    }
  }

  getActionButtonText() {
    switch (this.currentStep()) {
      case ShoppingCartStep.CART:
        return 'Proceed to Payment';
      case ShoppingCartStep.PAYMENT:
        return this.paymentProcessing ? 'Processing...' : 'Complete Purchase';
      case ShoppingCartStep.CONFIRMATION:
        return 'Continue Shopping';
      default:
        return 'Continue';
    }
  }

  /**
   * Determines whether the current cart is a single-item purchase for a renewal quote
   * request whose selected quote is set as Direct Bill. In the multi-item purchase flow
   * there are no changes and no attempt to redirect to the carrier.
   */
  isSingleRenewalDirectBill(): boolean {
    if (this.currentStep() !== ShoppingCartStep.CART) {
      return false;
    }

    const cartItems = this.items();
    if (cartItems.length !== 1) {
      return false;
    }

    const item = cartItems[0];
    if (!isQuoteRequestItem(item)) {
      return false;
    }

    const data: any = item.data;
    const isRenewal = data?.renewal_quote_request_id > 0;
    const billingType = (data?.selected_quote?.billing_type || '').toString().toLowerCase();
    const isDirectBill = billingType.includes('direct');

    return isRenewal && isDirectBill && !!data?.selected_quote?.jump_url;
  }

  private buildDirectBillUrl(): string {
    const item = this.items()[0];
    const jumpUrl = (item && isQuoteRequestItem(item)) ? ((item.data as any)?.selected_quote?.jump_url || '') : '';
    if (!jumpUrl) {
      return '';
    }
    const accountUserId = this._userService.currentAccountUser()?.id ?? '';
    return `${jumpUrl}&linksrc=dani&actuid=${accountUserId}`;
  }

  openDirectBillDialog() {
    const url = this.buildDirectBillUrl();
    if (!url) {
      this.proceedToPayment();
      return;
    }

    const dialogRef = this._fuseConfirmationService.open({
      title: 'Proceed to Payment',
      message:
        'You will be redirected to the surety\'s website for direct payment.<br>' +
        'Your payment will be reflected here within 1 business day.',
      icon: {
        show: true,
        name: 'heroicons_outline:external-link',
        color: 'primary',
      },
      actions: {
        confirm: {
          show: true,
          label: 'Proceed',
          color: 'primary',
        },
        cancel: {
          show: true,
          label: 'Cancel',
        },
      },
      dismissible: true,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        window.open(url, '_blank', 'noopener');
      }
    });
  }

  handlePrimaryAction() {
    switch (this.currentStep()) {
      case ShoppingCartStep.CART:
        this.proceedToPayment();
        break;
      case ShoppingCartStep.PAYMENT:
        this.processPayment();
        break;
      case ShoppingCartStep.CONFIRMATION:
        this.continueShopping();
        break;
    }
  }

  // Step navigation methods
  proceedToPayment() {
    if (this.items().length === 0) {
      console.warn('Cannot proceed to payment with empty cart');
      return;
    }
    // Disable overlay when proceeding to payment to prevent accidental closes
    this.overlaySettingsChanged.emit({
      transparentOverlay: false,
      disableOverlay: false
    });
    this.currentStep.set(ShoppingCartStep.PAYMENT);
    this.scrollToTop();
  }

  completePurchase() {
    console.log('Processing purchase...');
    // TODO: Implement actual purchase logic
    this.currentStep.set(ShoppingCartStep.CONFIRMATION);
    this.scrollToTop();
  }

  continueShopping() {
    this._shoppingCartService.closeDrawer();
  }

  goBackToCart() {
    console.log('goBackToCart() called - resetting overlay settings');
    // Re-enable overlay when going back to cart
    this.overlaySettingsChanged.emit({
      transparentOverlay: true,
      disableOverlay: true
    });
    this.currentStep.set(ShoppingCartStep.CART);
    this.scrollToTop();
  }

  goBackToPayment() {
    this.currentStep.set(ShoppingCartStep.PAYMENT);
    this.scrollToTop();
  }

  // Method to handle back navigation from drawer
  handleDrawerBackClick() {
    console.log('handleDrawerBackClick() called, current step:', ShoppingCartStep[this.currentStep()]);
    switch (this.currentStep()) {
      case ShoppingCartStep.PAYMENT:
        this.goBackToCart();
        break;
      case ShoppingCartStep.CONFIRMATION:
        this.goBackToPayment();
        break;
      // Cart step doesn't have a back action
    }
  }

  // Payment-related methods
  onPaymentFormValidityChange(isValid: boolean) {
    this.isPaymentFormValid = isValid;
    this._changeDetectorRef.markForCheck();
  }

  onPaymentProcessed(result: PaymentProcessResult) {
    this.paymentProcessing = false;
    this.paymentResult = result;

    if (result.success) {
      // Extract order number from result if available
      this.orderNumber = result.transactionId || null;
      this.completePurchase();
    } else {
      // Error handling is done in the payment component
      // Just make sure we're not stuck in processing state
      console.error('Payment failed:', result.error);
    }

    this._changeDetectorRef.markForCheck();
  }

  processPayment() {
    if (!this.isPaymentFormValid || this.paymentProcessing || !this._paymentComponent) {
      return;
    }

    this.paymentProcessing = true;
    this._changeDetectorRef.markForCheck();

    // Trigger the payment component's processPayment method
    this._paymentComponent.processPayment();
  }

  // Helper method to emit step change events
  private emitStepChangeEvent(step: ShoppingCartStep) {
    let title: string;
    let showBackButton: boolean;
    let canGoBack: boolean;

    switch (step) {
      case ShoppingCartStep.CART:
        title = 'Shopping Cart';
        showBackButton = false;
        canGoBack = false;
        break;
      case ShoppingCartStep.PAYMENT:
        title = 'Payment Information';
        showBackButton = true;
        canGoBack = true;
        break;
      case ShoppingCartStep.CONFIRMATION:
        title = 'Order Confirmation';
        showBackButton = true;
        canGoBack = true;
        break;
      default:
        title = 'Shopping Cart';
        showBackButton = false;
        canGoBack = false;
    }

    this.stepChanged.emit({
      step,
      title,
      showBackButton,
      canGoBack
    });
  }

  onRemoveItem(item: ShoppingCartItem<any>) {
    this._shoppingCartService.removeItem(item);
  }

  onUpdateQuantity(event: { item: ShoppingCartItem<any>, quantity: number }) {
    // Update the item quantity
    const updatedItem = { ...event.item, quantity: event.quantity };
    this._shoppingCartService.addItem(updatedItem); // This will replace the existing item
  }

  onViewDetails(item: ShoppingCartItem<any>) {
    // console.log('View details for item:', item);
    // TODO: Implement view details functionality
    // Needs to utilize MKs new quote request drawer wrapper.

  }

  onItemUpdated(item: ShoppingCartItem<any>) {
    // console.log('Item updated:', item);
    // This method triggers change detection to update cart totals
    // The reactive calculation methods in the item will be called automatically
    this._changeDetectorRef.markForCheck();
  }

  shouldExpandAccordion(): boolean {
    // Expand accordion only when there's exactly 1 item in the cart
    // This provides immediate detail view for single items, but keeps UI clean with multiple items
    return this.items().length === 1;
  }

  // Cart calculation methods are now handled by CIPXCartSummaryComponent

  // Type guard methods for template use
  isQuoteRequestItem(item: ShoppingCartItem): boolean {
    return isQuoteRequestItem(item);
  }

  isRiderItem(item: ShoppingCartItem): boolean {
    return isRiderItem(item);
  }

  scrollToTop(): void {
    // Use setTimeout to ensure the view is updated after changes
    setTimeout(() => {
      if (this.shoppingCartScroll?.nativeElement) {
        this.shoppingCartScroll.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
        // console.log('scrolled to top using ViewChild');
      } else {
        // console.log('ViewChild not available, trying fallback approach');
        this.fallbackScrollToTop();
      }
    }, 50); // Small delay to ensure DOM is updated
  }

  private fallbackScrollToTop(): void {
    // Fallback: try to find the scrollable element by class
    const scrollableElement = document.querySelector('.cipx-scrollable-content');
    if (scrollableElement) {
      scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
      // console.log('scrolled to top using fallback selector');
    } else {
      // console.log('Could not find scrollable element');
    }
  }

  // Confirmation step event handlers
  onPurchaseAnother() {
    // Reset cart and go back to cart step
    this._shoppingCartService.clear();
    this.currentStep.set(ShoppingCartStep.CART);
    this.orderNumber = null;
    this.paymentResult = null;
  }

  onPurchaseAnotherSimilar() {
    // Keep similar items logic could be implemented here
    // console.log('Purchase another similar item');
    this.onPurchaseAnother(); // For now, just reset
  }

  onRequestQuote() {
    // Navigate to quote request form
    // console.log('Request new quote');
    // This would typically navigate to the quote request intake form
  }

  onViewBonds() {
    this._router.navigate(['/dashboard']);
    this.closeDrawer.emit();
  }

  onDeclineQuote(item: ShoppingCartItem<any>) {
    // console.log('Decline quote for item:', item);
    this._openQuoteRequestsWidgetService.declineQuote(+item.id).subscribe((response) => {
      if (response && response.quote_request) {
        this._openQuoteRequestsWidgetService.loadQuoteRequest(+item.id).subscribe((r) => {
          if (r) {
            this._openQuoteRequestsWidgetService.selectedOpenQR.set(r);
            this._shoppingCartService.removeItem(item);
            this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe();
            this._quoteRequestsListService.triggerRefresh.emit();
            this._changeDetectorRef.markForCheck();
            this._cathyService.success('Quote declined successfully. Removing from your cart.');
          } else {
            this._cathyService.error('Failed to decline quote. Please try again.');
          }
        });
      } else {
        this._cathyService.error('Failed to decline quote. Please try again.');
      }
    });
  }


  sendMessageFromDrawer(data: { item: ShoppingCartItem<any>, messageType: ShoppingCartMessageType }) {
    const item = data.item;
    const messageType = data.messageType;
    this._openQuoteRequestsWidgetService.loadQuoteRequest(+item.id).subscribe((qr) => {
      if (qr) {
        let subject = `Question about order ${qr?.order_number}`;
        if (messageType === 'make-a-change') {
          subject = `Change needed for ${qr?.quote_request_bond?.company_name || `${qr?.user_first_name ?? ''} ${qr?.user_last_name ?? ''}`.trim()} ${qr?.order_number}`;
        }

        const principal = qr?.quote_request_bond?.company_name || `${qr?.user_first_name ?? ''} ${qr?.user_last_name ?? ''}`.trim();
        const formatUSD = (v: any) => {
          const n = typeof v === 'number' ? v : Number(v);
          return Number.isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(v ?? '');
        };
        const penalty = formatUSD(qr?.bond_amount ?? qr?.quote_request_bond?.bond_amount);
        const state = qr?.state_abbreviation ?? qr?.quote_request_bond?.state_abbreviation;
        const suretyLabel = qr?.surety_bond_label ?? qr?.quote_request_bond?.surety_bond_label;
        const body = `<br><br>Regarding ${penalty}, ${state}, ${suretyLabel} for ${principal}`;

        const t  =  {
          task_label: subject,
            task_description: body,
            task_set_id: (item as any)?.task_set_id ?? null,
            assignees: [],
            task_related_entity_key: qr.quote_request_id,
            task_related_entity_type: 'cipquotereq'
        };

        this._drawerService.openDrawer(MessageComposeDrawerWrapperComponent, {
          data: {
            title: `Send Message · Order ${qr?.order_number}`,
            qr: qr as any,
            task: t as any,
          },
        });
      } else {
        this._cathyService.error('Failed to decline quote. Please try again.');
      }
    });


  }
}
